import React, { useEffect, useState } from "react";
import { useTheme } from "@material-ui/core";
import { useLocation, useRouteMatch } from "react-router";
import _ from "lodash";

import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import {
  fetchAllCancelSalesInfo,
  fetchAllUserInfo,
} from "../../../../services/salesApp/salesService";
import Pagination from "../../../../components/common/Pagination";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import { getCookie } from "../../../../utils/cookies";
import { saleListFilterObject } from "../../../../utils/consts/list";
import SaleReport from "../../../../components/common/SaleReport";
import SaleList from "../../../../components/common/SaleList";
import { CustomTheme } from "../../../../types/customTheme";
import { getFilterListFromArrayObject } from "../../../../utils/commonArrayMap";
import SalesReportInfoNode from "./SaleCategoryReportInfoNode";
import { fetchAllDiscountInfoSales } from "../../../../services/globalService";
import { getAllDeviceIdInfo } from "../../../../services/salesApp/shiftService";

/**
 * SalesReport Component:
 *
 * This component is responsible for displaying sales-related information and interactions,
 * including filtering, sorting, pagination, and error handling. It fetches data from the
 * backend APIs and renders the sales report and list accordingly. The component integrates
 * with user authentication to control access based on user permissions.
 */
const SalesReport: React.FunctionComponent<{}> = () => {
  const [salesNodeList, setSalesNodeList] = useState<any>([]);
  const [error, setError] = useState("");
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [headerName, setHeaderName] = useState("date");
  const [sortingMethod, setSortingMethod] = useState("DESC");
  const [disableButton, setDisableButton] = useState(false);
  const [filterData, setFilterData] = useState("");
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [userList, setUserList] = useState<any>();
  const [userListFilter, setUserListFilter] = useState<any>([]);
  const [isGetLocationInfo, setIsGetLocationInfo] = useState(false);
  const [isOpenSkeletonLoading, setIsOpenSkeletonLoading] = useState(true);
  const [filterDetails, setFilterDetails] = useState("");
  const [discountInfo, setDiscountInfo] = useState<any>({});
  const [isPaymentTypeLoading, setIsPaymentTypeLoading] = useState(false);
  const [isDiscountLoading, setIsDiscountLoading] = useState(false);
  const [openFilterCard, setOpenFilterCard] = useState(false);
  const [locationSelectedList, setLocationSelectedList] = useState([]);
  const [isSort, setIsSort] = useState(false);
  const [totalResult, setTotalResult] = useState(0);
  const [isActiveUserSwitch, setIsActiveUserSwitch] = useState(false);
  const [isCategoryError, setIsCategoryError] = useState(false);
  const [deviceIdObj, setDeviceIdObj] = useState<any>({});
  const [deviceIdErr, setDeviceIdErr] = useState(false);
  const [deviceSelectorList, setDeviceSelectorList] = useState<any>([]);
  const [nodes, setNodes] = useState<any>({ nodes: [] });
  const match: any = useRouteMatch();
  const idToken = getCookie("idToken");
  const { search } = useLocation();
  const params = new URLSearchParams(search);


/**
 * Convert device ID response to selector list format
 * @param {Object} deviceData - Raw device data from API
 * @returns {Array} - Formatted device list with id and label
 */
const convertDeviceIdToSelectorList = (deviceData: any) => {
  const deviceList: any = [];
  const uniqueLabels = new Set(); // Track unique labels
  
  // Check if deviceData is an object
  if (!_.isEmpty(deviceData) && typeof deviceData === 'object') {
    // Iterate through the object values
    Object.values(deviceData).forEach((device: any) => {
      // Only include devices with a valid dev_id
      if (device.dev_id && device.dev_id !== '-99') {
        // Use handleTerminal to get the device label
        const deviceLabel = `T-${device.prefix}`;
        
        // Create label using dev_id and the terminal name
        let label = deviceLabel;
        
        // Check if this label already exists
        if (!uniqueLabels.has(label)) {
          uniqueLabels.add(label); // Add to set to track duplicates
          
          console.log("devicedevice", device);
          deviceList.push({
            id: device.dev_id,
            label: label,
            // Keep original data for reference
            ...device
          });
        }
      }
    });
  }
  
  // Sort by dev_id (numeric)
  deviceList.sort((a: any, b: any) => {
    return parseInt(a.id) - parseInt(b.id);
  });
  
  return deviceList;
};

  /**
   * Fetch all pages of sales data recursively
   * @param {string} idToken - Authentication token
   * @param {string} locationId - Location ID
   * @param {string} sortingMethod - Sorting method (ASC/DESC)
   * @param {string} headerName - Header name for sorting
   * @param {string} filter - Filter criteria
   * @param {number} page - Current page number (0-based)
   * @param {array} allData - Accumulated data from previous pages
   * @returns {Promise<object>} - All data with metadata
   */
  const fetchAllPagesData = async (
    idToken: string,
    locationId: string,
    sortingMethod: string,
    headerName: string,
    filter: string,
    page: number = 0,
    allData: any[] = []
  ): Promise<any> => {
    try {
      const response = await fetchAllCancelSalesInfo(
        idToken,
        locationId,
        2000,
        page + 1, // API uses 1-based pagination
        sortingMethod,
        headerName,
        filter
      );

      // Check if response has the expected structure
      if (!response || !response.data) {
        console.error("Invalid response structure:", response);
        throw new Error("Invalid response from API");
      }

      // The API returns data directly as an array
      const { data, totalPages, totalResults } = response.data;
      
      // Ensure data is an array before spreading
      const newData = Array.isArray(data) ? data : [];
      
      // Add current page data to accumulated data
      const updatedData = [...allData, ...newData];
      
      // Check if this is the last page
      if (page < totalPages - 1) {
        // Recursive call for next page
        return fetchAllPagesData(
          idToken,
          locationId,
          sortingMethod,
          headerName,
          filter,
          page + 1,
          updatedData
        );
      }
      
      // All data fetched
      return {
        data: updatedData,
        totalResults: totalResults || 0,
        totalPages: totalPages || 0,
        currentPage: totalPages ? totalPages - 1 : 0
      };
    } catch (error) {
      console.error("Error fetching paginated data:", error);
      throw error;
    }
  };

  /* Get sale list using query params */
  const getSaleFilterInfo = async (
    sortingMethod: any,
    headerName: any,
    filter: any,
  ) => {
    // Check if idToken exists
    if (!idToken) {
      setError("Authentication token not found");
      setIsLoadingPage(false);
      setIsOpenSkeletonLoading(false);
      return;
    }

    try {
      setIsOpenSkeletonLoading(true);
      
      // Fetch all pages of data
      const result = await fetchAllPagesData(
        idToken,
        match.params.locationId,
        sortingMethod,
        headerName,
        filter
      );
      
      setSalesNodeList(result.data);
      setTotalPages(result.totalPages);
      setTotalResult(result.totalResults);
      setIsLoadingPage(false);
      setIsPaymentTypeLoading(false);
      setIsDiscountLoading(false);
    } catch (err) {
      console.error("Error in getSaleFilterInfo:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoadingPage(false);
      setIsPaymentTypeLoading(false);
      setIsDiscountLoading(false);
    }
    setIsOpenSkeletonLoading(false);
  };

  /* The user details required for the filter and the user details required to be 
    displayed in the table are entered into the state. */
  const handleUserList = (userDetails: any) => {
    const userArray: any = [];
    const userObject: any = {};
    // Used the filter
    Object.values(userDetails).map((data: any) => {
      userArray.push({ id: data.id, label: data.name });
      if (!_.isEmpty(userList)) {
        if (_.isEmpty(userList[data.id])) {
          userObject[data.id] = { id: data.id, label: data.name };
        }
      } else {
        userObject[data.id] = { id: data.id, label: data.name };
      }
    });
    setUserListFilter(userArray);

    // Used the table
    setUserList((prevState: any) => ({ ...prevState, ...userObject }));
  };

  /* Get user details list */
  const getUserInfo = async (locationId: any) => {
    if (!idToken) {
      setError("Authentication token not found");
      return;
    }
    try {
      const res = await fetchAllUserInfo(idToken, locationId);
      if (!_.isEmpty(res.data.data)) {
        handleUserList(res.data.data);
      } else {
        setUserListFilter([]);
      }
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /* Get location details. */
  const getAllFilterLocation = async (searchName: any) => {
    fetchAllFilterLocations(searchName)
      .then((res) => {
        const locationList: any = [];
        if (!_.isEmpty(res.data.data)) {
          /* Setting up the list of locations as needed to select locations. */
          res.data.data.map((location: any) =>
            locationList.push({
              id: location.id,
              label: location.businessDisplayName,
            }),
          );
          const locationName = getFilterListFromArrayObject(
            locationList,
            match.params.locationId,
          );
        }
        setLocationSelectorList(locationList);
        setIsGetLocationInfo(true);
      })
      .catch(() => {
        setIsLoadingPage(false);
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      });
  };

  const getAllDiscountInfo = async () => {
    try {
      const res = await fetchAllDiscountInfoSales(match.params.locationId);
      setDiscountInfo(res.data.data);
      getAllFilterLocation("");
    } catch (err) {
      console.error("Error fetching discount info:", err);
      setIsLoadingPage(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  const getAllDeviceId = async () => {
    getAllDeviceIdInfo(match.params.locationId)
      .then(async (res: any) => {
        setDeviceIdObj(res.data.data);
        
        // Convert device data to selector list format using handleTerminal
        const formattedDeviceList = await convertDeviceIdToSelectorList(res.data.data);
        setDeviceSelectorList(formattedDeviceList);
    getAllDiscountInfo();
        
        setDeviceIdErr(false);
      })
      .catch(() => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
        setDeviceIdErr(true);
      });
  };

  useEffect(() => {
    document.title = "Sale - Sale Report";
    setIsLoadingPage(true);
    getAllDeviceId();
  }, []);

  // Get filter data
  const handleFilterData = (filterData: any) => {
    setFilterData(filterData);
    setIsOpenSkeletonLoading(true);
    getSaleFilterInfo(sortingMethod, headerName, filterData);
  };

  const handleOnClickText = () => {
    setOpenFilterCard(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* Get location list API call after typing. */
  const handleLocationSelectorTypingList = () => {};

  return (
    <>
      <SaleReport
        handleFilterData={handleFilterData}
        locationSelectorList={locationSelectorList}
        handleLocationSelectorTypingList={handleLocationSelectorTypingList}
        isGetLocationInfo={isGetLocationInfo}
        getUserInfo={getUserInfo}
        userListFilter={userListFilter}
        setFilterDetails={setFilterDetails}
        availableFilter={saleListFilterObject}
        isOpenSkeletonLoading={isOpenSkeletonLoading}
        nodeList={salesNodeList}
        filterDetails={filterDetails}
        title=""
        isChart={false}
        isUserGet
        isSale
        locationSelectedList={locationSelectedList}
        setLocationSelectedList={setLocationSelectedList}
        openFilterCard={openFilterCard}
        setOpenFilterCard={setOpenFilterCard}
        handleOnClickText={handleOnClickText}
        topic="Sales Report"
        isReport={false}
        deviceSelectorList={deviceSelectorList}
      >
        {!isOpenSkeletonLoading && (
          <SalesReportInfoNode
            filterDetails={filterDetails}
            locationSelectedList={locationSelectedList}
            nodeList={salesNodeList}
            handleOnClickText={handleOnClickText}
            userList={userList}
            isActiveUserSwitch={isActiveUserSwitch}
            setIsActiveUserSwitch={setIsActiveUserSwitch}
            setIsOpenSkeletonLoading={setIsOpenSkeletonLoading}
            isCategoryError={isCategoryError}
            nodes={nodes}
            setNodes={setNodes}
            discountInfo={discountInfo}
            deviceIdObj={deviceIdObj}
          />
        )}
      </SaleReport>
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(SalesReport, Authorities.SALE_READ);